import {
  bindColor,
  bindTextStyle,
  brandNewDocument,
  brandScale,
  brandTypography,
} from "../brand/apply";
import { type BrandTokens, brandColor, brandTextStyle } from "../brand/tokens";
import { contrastColor } from "./color";
import { MASK_PRESETS } from "./masks";
import { createImageElement, createShapeElement, createTextElement } from "./ops";
import { type FormatPreset, getPreset } from "./presets";
import {
  SCENE_SCHEMA_VERSION,
  type SceneDocument,
  type SceneElement,
  sceneDocumentSchema,
  type TextStyle,
  textStyleSchema,
} from "./schema";

export function createEmptyDocument(preset: FormatPreset, background = "#ffffff"): SceneDocument {
  return {
    schemaVersion: SCENE_SCHEMA_VERSION,
    kind: preset.kind,
    format: {
      preset: preset.id,
      width: preset.width,
      height: preset.height,
      print: preset.print ?? null,
    },
    pages: [{ id: "page_1", background, children: [] }],
    elements: {},
    ...(preset.kind === "video"
      ? { timeline: { fps: 30, clips: [], audio: [], captions: { overrides: {} } } }
      : {}),
  };
}

export interface SocialPostText {
  text: string;
  role?: "headline" | "subline" | "body" | "caption";
  /** Hex value or, with a brand kit, the key of a brand colour. */
  color?: string;
  align?: TextStyle["align"];
}

export interface SocialPostInput {
  presetId: string;
  /** Hex value or, with a brand kit, the key of a brand colour. */
  background?: string;
  texts: SocialPostText[];
  imageAssetId?: string;
  /** Form of the image (id of `MASK_PRESETS`, e.g. "circle"): a centred framed picture. */
  imageMask?: string;
  /** Hex value or, with a brand kit, the key of a brand colour. */
  accentColor?: string;
  /**
   * Brand kit: texts use its text styles and colours and stay linked to it.
   * Without one (null: the organization has no kit) the post uses neutral defaults.
   */
  brand?: BrandTokens | null;
}

const ROLE_TEXT_STYLE: Record<NonNullable<SocialPostText["role"]>, string> = {
  headline: "heading-1",
  subline: "subline",
  body: "body",
  caption: "caption",
};

const ROLE_SIZE: Record<NonNullable<SocialPostText["role"]>, { size: number; weight: number }> = {
  headline: { size: 0.085, weight: 800 },
  subline: { size: 0.05, weight: 600 },
  body: { size: 0.036, weight: 400 },
  caption: { size: 0.026, weight: 500 },
};

/**
 * Builds a simple, well-spaced social post layout. Used by the MCP tool
 * `create_social_post` and as a starting point in the app.
 */
export function createSocialPost(input: SocialPostInput): SceneDocument {
  const preset = getPreset(input.presetId);
  if (!preset) throw new Error(`Unbekanntes Format: ${input.presetId}`);
  const brand = input.brand;
  /** A brand colour key given instead of a hex value. */
  const brandKey = (value: string | undefined) =>
    brand && value && !value.startsWith("#") && brandColor(brand, value) ? value : undefined;
  const hex = (value: string | undefined) => {
    const key = brandKey(value);
    return key ? brandColor(brand!, key)!.color : value;
  };
  let doc = createEmptyDocument(preset, hex(input.background) ?? "#ffffff");
  if (brand) {
    const backgroundKey = input.background ? brandKey(input.background) : "background";
    doc = brandNewDocument(doc, brand);
    if (input.background) {
      const page = doc.pages[0]!;
      page.background = hex(input.background) ?? "#ffffff";
      page.brand = backgroundKey ? { background: backgroundKey } : undefined;
    }
  }
  const background = doc.pages[0]!.background as string;
  const page = doc.pages[0]!;
  const scale = brandScale(doc);
  const { width, height } = preset;
  const margin = Math.round(width * 0.08);
  const contentWidth = width - margin * 2;
  const add = (element: SceneElement) => {
    doc.elements[element.id] = element;
    page.children.push(element.id);
  };

  let cursorY = margin;
  if (input.imageAssetId) {
    const mask = MASK_PRESETS.find((preset) => preset.id === input.imageMask)?.mask;
    if (mask) {
      const side = Math.round(Math.min(width * 0.6, height * 0.42));
      const image = createImageElement(input.imageAssetId, {
        x: Math.round((width - side) / 2),
        y: margin,
        width: side,
        height: side,
      });
      add({ ...image, mask: { ...mask } });
      cursorY = margin + side + margin;
    } else {
      const imageHeight = Math.round(height * 0.5);
      add(createImageElement(input.imageAssetId, { x: 0, y: 0, width, height: imageHeight }));
      cursorY = imageHeight + margin;
    }
  }

  if (input.accentColor) {
    const accent = createShapeElement("rect", {
      x: margin,
      y: cursorY,
      width: Math.round(width * 0.12),
      height: Math.max(8, Math.round(width * 0.012)),
      fill: hex(input.accentColor) ?? input.accentColor,
      radius: 4,
    });
    const key = brandKey(input.accentColor);
    if (key) bindColor(accent, "fill", key, brand!);
    add(accent);
    cursorY += Math.round(width * 0.05);
  }

  const defaultColor = contrastColor(background);
  for (const [index, item] of input.texts.entries()) {
    const role = item.role ?? (index === 0 ? "headline" : "body");
    const brandStyle = brand ? brandTextStyle(brand, ROLE_TEXT_STYLE[role]) : undefined;
    const { size, weight } = ROLE_SIZE[role];
    const typography = brandStyle
      ? brandTypography(brandStyle, scale)
      : {
          fontFamily: "Inter",
          fontSize: Math.round(width * size),
          fontWeight: weight,
          lineHeight: role === "headline" ? 1.08 : 1.3,
          uppercase: false,
        };
    const { fontSize, lineHeight } = typography;
    const charsPerLine = Math.max(8, Math.floor(contentWidth / (fontSize * 0.55)));
    const lines = Math.max(1, Math.ceil(item.text.length / charsPerLine));
    const boxHeight = Math.ceil(lines * fontSize * lineHeight) + 8;
    const style = textStyleSchema.parse({
      ...typography,
      color: hex(item.color) ?? defaultColor,
      align: item.align ?? "left",
    });
    const element = createTextElement({
      text: item.text,
      style,
      x: margin,
      y: cursorY,
      width: contentWidth,
      height: boxHeight,
    });
    if (brand && brandStyle) {
      bindTextStyle(element, brandStyle.key, brand, scale, { withColor: !item.color });
    }
    const colorKey = brandKey(item.color);
    if (colorKey) bindColor(element, "color", colorKey, brand!);
    add(element);
    cursorY += boxHeight + Math.round(fontSize * 0.6);
  }

  return sceneDocumentSchema.parse(doc);
}
